import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import PropTypes from 'prop-types';
import { globalPropTypes } from 'helpers';
import { PageContent, Spinner, CisContainer, ButtonIcon, Modal, Row } from 'components';
import CisTable from 'components/Table/CisTable';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import CisPagination from 'components/Table/CisPagination';
import { useHistory } from 'react-router-dom';

const UnknownImports = ({
  appType,
  loading,
  list,
  loadList,
  setPage,
  setPerPage,
  setSort,
  setFilters,
  removeFilters,
  acceptEntry,
  rejectEntry,
  setProfile,
}) => {
  const { filters, items, page, pages, perPage, sort, totalItems } = list;
  const { t } = useTranslation();
  const [openAcceptModal, setOpenAcceptModal] = useState(false);
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const history = useHistory();

  useEffect(() => {
    setPage(1);
    loadList();
    // eslint-disable-next-line
  }, [perPage, sort]);

  const getSortDirection = (key) => {
    if (!sort) return null;
    const [sortKey, sortDir] = sort.split('.');
    return sortKey === key ? sortDir : null;
  };

  const handleColumnSort = (key, direction) => {
    if (direction) {
      setSort(`${key}.${direction}`);
    } else {
      setSort(null);
    }
  };

  const handleColumnFilterChange = (key, value) => {
    setFilters({ name: key, value });
    setPage(1);
    loadList();
  };

  return (
    <PageContent>
      <CisContainer>
        <CisTable>
          <CisTable.Head>
            <CisTable.Row>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.firstName || ''}
                  onFilter={(val) => handleColumnFilterChange('firstName', val)}
                  filterPlaceholder={t('page.user.cis.unknownImports.firstNamePlaceholder')}
                  label={t('page.user.cis.unknownImports.firstName')}
                  sort
                  sortDirection={getSortDirection('firstName')}
                  onSort={(dir) => handleColumnSort('firstName', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.lastName || ''}
                  onFilter={(val) => handleColumnFilterChange('lastName', val)}
                  filterPlaceholder={t('page.user.cis.unknownImports.lastNamePlaceholder')}
                  label={t('page.user.cis.unknownImports.lastName')}
                  sort
                  sortDirection={getSortDirection('lastName')}
                  onSort={(dir) => handleColumnSort('lastName', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.email || ''}
                  onFilter={(val) => handleColumnFilterChange('email', val)}
                  filterPlaceholder={t('page.user.cis.unknownImports.emailPlaceholder')}
                  label={t('page.user.cis.unknownImports.email')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="datepicker"
                  filterValue={filters.birthDate || ''}
                  onFilter={(val) => handleColumnFilterChange('birthDate', val)}
                  filterPlaceholder={t('page.user.cis.unknownImports.birthDate')}
                  label={t('page.user.cis.unknownImports.birthDate')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.certificateNumber || ''}
                  onFilter={(val) => handleColumnFilterChange('certificateNumber', val)}
                  filterPlaceholder={t('page.user.cis.unknownImports.certificateNumberPlaceholder')}
                  label={t('page.user.cis.unknownImports.certificateNumber')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.certType || ''}
                  onFilter={(val) => handleColumnFilterChange('certType', val)}
                  filterPlaceholder={t('page.user.cis.unknownImports.certType')}
                  label={t('page.user.cis.unknownImports.certType')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="datepicker"
                  filterValue={filters.issueDate || ''}
                  onFilter={(val) => handleColumnFilterChange('issueDate', val)}
                  filterPlaceholder={t('page.user.cis.unknownImports.issueDate')}
                  label={t('page.user.cis.unknownImports.issueDate')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="datepicker"
                  filterValue={filters.validUntil || ''}
                  onFilter={(val) => handleColumnFilterChange('validUntil', val)}
                  filterPlaceholder={t('page.user.cis.unknownImports.validUntil')}
                  label={t('page.user.cis.unknownImports.validUntil')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.reason || ''}
                  onFilter={(val) => handleColumnFilterChange('reason', val)}
                  filterPlaceholder={t('page.user.cis.unknownImports.reasonPlaceholder')}
                  label={t('page.user.cis.unknownImports.reason')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyActionHeading>
                <TableHeaderCell
                  label={t('page.user.cis.unknownImports.actions')}
                  labelAlign="center"
                  isAction={true}
                />
              </CisTable.StickyActionHeading>
            </CisTable.Row>
          </CisTable.Head>
          <CisTable.Body>
            {loading ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={11}>
                  <Spinner />
                </CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && !items.length ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={11}>{t('global.noResultsFound')}</CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && items.length
              ? items.map((item) => (
                  <CisTable.Row key={item.id}>
                    <CisTable.Cell>
                      <CisTable.Label>
                        {t('page.user.cis.unknownImports.firstName')}:
                      </CisTable.Label>
                      {item.firstName}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <CisTable.Label>{t('page.user.cis.unknownImports.lastName')}:</CisTable.Label>
                      {item.lastName}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <CisTable.Label>{t('page.user.cis.unknownImports.email')}:</CisTable.Label>
                      {item.email}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <CisTable.Label>
                        {t('page.user.cis.unknownImports.birthDate')}:
                      </CisTable.Label>
                      {item.birthDate && moment(item.birthDate).format('DD/MM/YYYY')}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <CisTable.Label>
                        {t('page.user.cis.unknownImports.certificateNumber')}:
                      </CisTable.Label>
                      {item.certificateNumber}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <CisTable.Label>{t('page.user.cis.unknownImports.certType')}:</CisTable.Label>
                      {item.certType}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <CisTable.Label>
                        {t('page.user.cis.unknownImports.issueDate')}:
                      </CisTable.Label>
                      {item.issueDate && moment(item.issueDate).format('DD/MM/YYYY')}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <CisTable.Label>
                        {t('page.user.cis.unknownImports.validUntil')}:
                      </CisTable.Label>
                      {item.validUntil && moment(item.validUntil).format('DD/MM/YYYY')}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <CisTable.Label>{t('page.user.cis.unknownImports.reason')}:</CisTable.Label>
                      {item.reason}
                    </CisTable.Cell>
                    <CisTable.StickyActionCell>
                      <CisTable.Label>{t('page.user.cis.unknownImports.actions')}:</CisTable.Label>
                      <Row style={{ gap: '3px' }}>
                        <ButtonIcon
                          icon="check"
                          title={t('page.user.cis.unknownImports.accept')}
                          onClick={() => {
                            setOpenAcceptModal(true);
                            setSelectedItem(item);
                          }}
                        />
                        <ButtonIcon
                          icon="trash"
                          title={t('page.user.cis.unknownImports.reject')}
                          onClick={() => {
                            setOpenRejectModal(true);
                            setSelectedItem(item);
                          }}
                        />
                        <ButtonIcon
                          icon="edit"
                          title={t('page.user.cis.unknownImports.edit')}
                          onClick={() => {
                            const dateFields = ['validUntil', 'issueDate', 'birthDate'];
                            const itemWithDates = { ...item };
                            dateFields.forEach((field) => {
                              if (itemWithDates[field]) {
                                itemWithDates[field] = new Date(itemWithDates[field]);
                              }
                            });
                            setProfile(itemWithDates);
                            history.push(`/${appType}/cis/unknown-imports/edit`);
                          }}
                        />
                      </Row>
                    </CisTable.StickyActionCell>
                  </CisTable.Row>
                ))
              : null}
          </CisTable.Body>
        </CisTable>
        <CisPagination
          styled
          currentPage={page}
          totalPages={pages}
          totalItems={totalItems}
          perPage={perPage}
          onPageChange={(payload) => {
            setPage(payload);
            loadList();
          }}
          onPerPageChange={({ value }) => setPerPage(value)}
        />
        <Modal
          heading={t('page.user.cis.unknownImports.confirmAccept')}
          isOpen={openAcceptModal && selectedItem?.id}
          confirm={() => {
            acceptEntry(selectedItem.id);
            setOpenAcceptModal(false);
          }}
          cancel={() => setOpenAcceptModal(false)}
          onRequestClose={() => setOpenAcceptModal(false)}
        />
        <Modal
          heading={t('page.user.cis.unknownImports.confirmReject')}
          isOpen={openRejectModal && selectedItem?.id}
          confirm={() => {
            rejectEntry(selectedItem.id);
            setOpenRejectModal(false);
          }}
          cancel={() => setOpenRejectModal(false)}
          onRequestClose={() => setOpenRejectModal(false)}
        />
      </CisContainer>
    </PageContent>
  );
};

UnknownImports.propTypes = {
  loading: PropTypes.bool,
  list: globalPropTypes.ListPropsTypes.isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  removeFilters: PropTypes.func.isRequired,
  acceptEntry: PropTypes.func.isRequired,
  rejectEntry: PropTypes.func.isRequired,
};

UnknownImports.defaultProps = {
  loading: false,
};

export default UnknownImports;
